import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../hooks/useI18n';
import '../styles/TutorialOverlay.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StepDef {
  key: string;
  selectors: string[];
  route: string;
  gradient: string;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type Phase = 'welcome' | 'touring' | 'complete' | 'idle';

interface Props {
  onComplete?: () => void;
}

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS: StepDef[] = [
  { key: 'library',     selectors: ['a[href="/library"]',        '[data-nav="library"]',     '[href*="/library"]'],        route: '/library',        gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { key: 'categories',  selectors: ['a[href="/categories"]',      '[data-nav="categories"]',  '[href*="/categories"]'],      route: '/categories',     gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { key: 'favorites',   selectors: ['a[href="/favorites"]',       '[data-nav="favorites"]',   '[href*="/favorites"]'],       route: '/favorites',      gradient: 'linear-gradient(135deg, #fbc531 0%, #f0932b 100%)' },
  { key: 'collections', selectors: ['a[href="/collections"]',     '[data-nav="collections"]', '[href*="/collections"]'],     route: '/collections',    gradient: 'linear-gradient(135deg, #4facfe 0%, #00c2ff 100%)' },
  { key: 'accounting',  selectors: ['a[href="/accounting"]',      '[data-nav="accounting"]',  '[href*="/accounting"]'],      route: '/accounting',     gradient: 'linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%)' },
  { key: 'ai',          selectors: ['a[href="/ai-assistant"]',    '[data-nav="ai"]',          '[href*="/ai-assistant"]'],    route: '/ai-assistant',   gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' },
  { key: 'security',    selectors: ['a[href="/hidden-folders"]',  '[data-nav="security"]',    '[href*="/hidden-folders"]'],  route: '/hidden-folders', gradient: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)' },
  { key: 'calendar',    selectors: ['a[href="/calendar"]',        '[data-nav="calendar"]',    '[href*="/calendar"]'],        route: '/calendar',       gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)' },
  { key: 'audit',       selectors: ['a[href="/audit"]',           '[data-nav="audit"]',       '[href*="/audit"]'],           route: '/audit',          gradient: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)' },
  { key: 'settings',    selectors: ['a[href="/settings"]',        '[data-nav="settings"]',    '[href*="/settings"]'],        route: '/settings',       gradient: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)' },
];

const STORAGE_DONE = 'elvoaq_tutorial_done';
const STORAGE_SEEN = 'elvoaq_tutorial_seen';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function findElement(selectors: string[]): Element | null {
  for (const sel of selectors) {
    try {
      const el = document.querySelector(sel);
      if (el) return el;
    } catch (_) { /* invalid selector */ }
  }
  return null;
}

function getSpotlightRect(el: Element, pad = 8): SpotlightRect {
  const r = el.getBoundingClientRect();
  return { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
}

// ─── Main component ───────────────────────────────────────────────────────────

const TutorialOverlay: React.FC<Props> = ({ onComplete }) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('idle');
  const [step, setStep] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number; left: number; arrowSide: 'top' | 'bottom' | 'left' | 'right';
  }>({ top: 0, left: 0, arrowSide: 'top' });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const posTimerRef = useRef<number | null>(null);
  // Track whether the touring tooltip has been shown before — we only
  // play the entrance slide-up animation on the very first mount.
  const tooltipEnteredRef = useRef(false);

  // ── Initial phase ────────────────────────────────────────────────────────
  useEffect(() => {
    const done = localStorage.getItem(STORAGE_DONE) === 'true';
    const seen = localStorage.getItem(STORAGE_SEEN) === 'true';
    if (!done && !seen) {
      const timer = window.setTimeout(() => setPhase('welcome'), 900);
      return () => window.clearTimeout(timer);
    }
  }, []);

  // ── Listen for manual trigger ────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(STORAGE_DONE);
      localStorage.removeItem(STORAGE_SEEN);
      setStep(0);
      setSpotlight(null);
      tooltipEnteredRef.current = false; // ensure entrance animation plays on replay
      setPhase('welcome');
    };
    window.addEventListener('elvoaq-start-tutorial', handler);
    window.addEventListener('tutorial:replay', handler);
    return () => {
      window.removeEventListener('elvoaq-start-tutorial', handler);
      window.removeEventListener('tutorial:replay', handler);
    };
  }, []);

  // ── Positioning ──────────────────────────────────────────────────────────
  const updatePosition = useCallback(() => {
    if (phase !== 'touring') return;
    const stepDef = STEPS[step];
    
    // Ensure the sidebar nav item is visible — scroll it into view
    const el = findElement(stepDef.selectors);
    if (el) {
      // Scroll the element into view within the sidebar scroll container
      const scrollContainer = el.closest('.sidebar-nav-scroll');
      if (scrollContainer) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Small delay to let scroll finish before measuring (reduced from 200ms to 80ms)
      setTimeout(() => {
        const measuredEl = findElement(stepDef.selectors);
        if (!measuredEl) return;
        const rect = getSpotlightRect(measuredEl);
        setSpotlight(rect);
        positionTooltip(rect);
      }, 80);
    } else {
      setSpotlight(null);
      setTooltipPos({ top: Math.max(16, window.innerHeight / 2 - 130), left: Math.max(16, window.innerWidth / 2 - 180), arrowSide: 'top' });
    }
  }, [phase, step]);

  const positionTooltip = useCallback((rect: SpotlightRect) => {
    const tipW = Math.min(360, window.innerWidth - 32);
    const tipH = 240;
    const margin = 14;
    let top: number, left: number;
    let arrowSide: 'top' | 'bottom' | 'left' | 'right' = 'top';

    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = window.innerWidth - (rect.left + rect.width);

    if (spaceBelow >= tipH + margin) {
      top = rect.top + rect.height + margin;
      left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 8));
      arrowSide = 'top';
    } else if (spaceRight >= tipW + margin) {
      top = Math.max(8, Math.min(rect.top + rect.height / 2 - tipH / 2, window.innerHeight - tipH - 8));
      left = rect.left + rect.width + margin;
      arrowSide = 'left';
    } else if (spaceAbove >= tipH + margin) {
      top = rect.top - tipH - margin;
      left = Math.max(8, Math.min(rect.left + rect.width / 2 - tipW / 2, window.innerWidth - tipW - 8));
      arrowSide = 'bottom';
    } else {
      top = Math.max(8, window.innerHeight / 2 - tipH / 2);
      left = Math.max(8, window.innerWidth / 2 - tipW / 2);
      arrowSide = 'top';
    }

    setTooltipPos({ top, left, arrowSide });
  }, []);

  // ── Navigate + re-position when step changes ─────────────────────────────
  useEffect(() => {
    if (phase !== 'touring') return;
    navigate(STEPS[step].route);
    // On mobile, open the sidebar so the nav item is visible for spotlighting
    if (window.innerWidth <= 768) {
      window.dispatchEvent(new CustomEvent('elvoaq-tutorial-sidebar', { detail: { open: true } }));
    }
    if (posTimerRef.current) window.clearTimeout(posTimerRef.current);
    posTimerRef.current = window.setTimeout(updatePosition, 150);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      if (posTimerRef.current) window.clearTimeout(posTimerRef.current);
    };
  }, [phase, step, navigate, updatePosition]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const startTour = () => {
    localStorage.setItem(STORAGE_SEEN, 'true');
    setStep(0);
    tooltipEnteredRef.current = false; // ensure entrance animation plays
    setPhase('touring');
  };

  const skipTour = () => {
    localStorage.setItem(STORAGE_SEEN, 'true');
    setPhase('idle');
    window.dispatchEvent(new CustomEvent('elvoaq-tutorial-sidebar', { detail: { open: false } }));
  };

  const nextStep = () => {
    if (step < STEPS.length - 1) {
      // Do NOT null the spotlight here — the CSS transition on .tut-spotlight
      // will smoothly move it to the new element. Nulling it causes a visible
      // flash while the 150+80ms delay resolves the new position.
      setStep(s => s + 1);
    } else {
      localStorage.setItem(STORAGE_DONE, 'true');
      setPhase('complete');
      onComplete?.();
    }
  };

  const prevStep = () => {
    if (step > 0) { setStep(s => s - 1); }
  };

  const finishTour = () => {
    localStorage.setItem(STORAGE_DONE, 'true');
    setPhase('idle');
    onComplete?.();
    window.dispatchEvent(new CustomEvent('elvoaq-tutorial-sidebar', { detail: { open: false } }));
  };

  const replayTour = () => {
    localStorage.removeItem(STORAGE_DONE);
    localStorage.removeItem(STORAGE_SEEN);
    setStep(0);
    setSpotlight(null);
    tooltipEnteredRef.current = false; // ensure entrance animation plays
    setPhase('welcome');
  };

  // ── Keyboard shortcut ────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'touring') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, step]); // stable deps — avoids adding/removing listener on every render

  // ── Idle phase ───────────────────────────────────────────────────────────
  if (phase === 'idle') return null;

  // ── Welcome modal ─────────────────────────────────────────────────────────
  if (phase === 'welcome') {
    return (
      <div className="tut-backdrop" role="dialog" aria-modal="true">
        <div className="tut-modal tut-modal--welcome">
          <button className="tut-modal-close" onClick={skipTour} aria-label="Dismiss">×</button>
          <div className="tut-modal__icon">👋</div>
          <h2 className="tut-modal__title">{t('tutorial.welcome.title' as any)}</h2>
          <p className="tut-modal__desc">{t('tutorial.welcome.desc' as any)}</p>
          <div className="tut-modal__actions">
            <button className="tut-btn tut-btn--primary" onClick={startTour}>
              {t('tutorial.startTour' as any)}
            </button>
            <button className="tut-btn tut-btn--ghost" onClick={skipTour}>
              {t('tutorial.exploreOwn' as any)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Completion modal ──────────────────────────────────────────────────────
  if (phase === 'complete') {
    return (
      <div className="tut-backdrop" role="dialog" aria-modal="true">
        <div className="tut-modal tut-modal--complete">
          <button className="tut-modal-close" onClick={finishTour} aria-label="Dismiss">×</button>
          <div className="tut-modal__confetti">🎉</div>
          <h2 className="tut-modal__title">{t('tutorial.done.title' as any)}</h2>
          <p className="tut-modal__desc">{t('tutorial.done.desc' as any)}</p>
          <ul className="tut-checklist">
            {STEPS.map((s) => (
              <li key={s.key} className="tut-checklist__item">
                <span className="tut-checklist__icon" style={{ background: s.gradient }}>✓</span>
                <span>{t(`tutorial.${s.key}.title` as any)}</span>
              </li>
            ))}
          </ul>
          <div className="tut-modal__actions">
            <button className="tut-btn tut-btn--primary" onClick={finishTour}>
              {t('tutorial.getStarted' as any)}
            </button>
            <button className="tut-btn tut-btn--ghost" onClick={replayTour}>
              {t('tutorial.replayTour' as any)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Touring: spotlight + tooltip ─────────────────────────────────────────
  const stepDef = STEPS[step];
  const tipW = Math.min(360, window.innerWidth - 32);

  return (
    <>
      {/* Full-screen spotlit overlay — click outside tooltip advances */}
      <div className="tut-spotlight-layer" aria-hidden="true" onClick={nextStep}>
        {spotlight && (
          <div
            className="tut-spotlight"
            style={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height }}
          />
        )}
      </div>

      {/* Floating tooltip */}
      <div
        ref={tooltipRef}
        className={[
          'tut-tooltip',
          `tut-tooltip--arrow-${tooltipPos.arrowSide}`,
          // Apply entrance class only on first render, then clear it after animation ends
          !tooltipEnteredRef.current ? 'tut-tooltip--entering' : '',
        ].join(' ').trim()}
        style={{ top: tooltipPos.top, left: tooltipPos.left, width: tipW }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={() => { tooltipEnteredRef.current = true; }}
      >
        {/* Progress dots */}
        <div className="tut-progress-dots">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={[
                'tut-progress-dot',
                i < step  ? 'tut-progress-dot--done'    : '',
                i === step ? 'tut-progress-dot--current' : '',
              ].join(' ').trim()}
              style={i === step ? { background: stepDef.gradient } : undefined}
            />
          ))}
        </div>

        {/* Step badge + title */}
        <div className="tut-tooltip__header">
          <span className="tut-tooltip__badge" style={{ background: stepDef.gradient }}>
            {step + 1}/{STEPS.length}
          </span>
          <h3 className="tut-tooltip__title">{t(`tutorial.${stepDef.key}.title` as any)}</h3>
        </div>

        <p className="tut-tooltip__desc">{t(`tutorial.${stepDef.key}.desc` as any)}</p>

        {/* Actions */}
        <div className="tut-tooltip__actions">
          <button className="tut-btn tut-btn--ghost tut-btn--sm" onClick={skipTour}>
            {t('tutorial.skip' as any)}
          </button>
          <div className="tut-tooltip__nav">
            {step > 0 && (
              <button className="tut-btn tut-btn--secondary tut-btn--sm" onClick={prevStep}>
                ← {t('tutorial.back' as any)}
              </button>
            )}
            <button
              className="tut-btn tut-btn--primary tut-btn--sm"
              onClick={nextStep}
              style={{ background: stepDef.gradient, border: 'none' }}
            >
              {step < STEPS.length - 1 ? `${t('tutorial.next' as any)} →` : t('tutorial.getStarted' as any)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── TutorialHelpButton ───────────────────────────────────────────────────────

export const TutorialHelpButton: React.FC = () => {
  const { t } = useI18n();

  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('elvoaq-start-tutorial'));
  };

  return (
    <button
      className="tut-help-btn"
      onClick={handleClick}
      title={t('tutorial.tourBadge' as any)}
      aria-label={t('tutorial.tourBadge' as any)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span>{t('tutorial.tourBadge' as any)}</span>
    </button>
  );
};

// ─── TutorialReplayButton (kept for backward compat) ─────────────────────────

export const TutorialReplayButton: React.FC = () => null;

export default TutorialOverlay;
